/**
 * GDPR Consent Manager for Nysa Learning Lab
 * Manages user consent preferences for analytics and tracking
 */

const CONSENT_KEY = 'nysa-cookie-consent';
const CONSENT_TIMESTAMP_KEY = 'nysa-consent-timestamp';
const CONSENT_DURATION = 365 * 24 * 60 * 60 * 1000; // 365 days

/**
 * Get current consent preferences
 */
export const getConsent = () => {
  if (typeof window === 'undefined') return null;

  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    const timestamp = localStorage.getItem(CONSENT_TIMESTAMP_KEY);

    if (!consent || !timestamp) return null;

    const consentAge = Date.now() - parseInt(timestamp, 10);

    // Check if consent has expired (365 days)
    if (consentAge > CONSENT_DURATION) {
      clearConsent();
      return null;
    }

    return JSON.parse(consent);
  } catch (error) {
    console.error('Error reading consent:', error);
    return null;
  }
};

/**
 * Set consent preferences
 */
export const setConsent = (preferences) => {
  if (typeof window === 'undefined') return;

  try {
    const fullPreferences = {
      analytics: preferences.analytics ?? false,
      functional: preferences.functional ?? true, // Always allow functional
      timestamp: Date.now(),
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(fullPreferences));
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, Date.now().toString());

    // Dispatch event for components to react
    window.dispatchEvent(
      new CustomEvent('consentChanged', { detail: fullPreferences })
    );
  } catch (error) {
    console.error('Error setting consent:', error);
  }
};

/**
 * Clear all consent preferences
 */
export const clearConsent = () => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(CONSENT_TIMESTAMP_KEY);

    window.dispatchEvent(
      new CustomEvent('consentChanged', {
        detail: { analytics: false, functional: true, timestamp: Date.now() },
      })
    );
  } catch (error) {
    console.error('Error clearing consent:', error);
  }
};

/**
 * Check if analytics consent is granted
 */
export const hasAnalyticsConsent = () => {
  const consent = getConsent();
  return consent?.analytics ?? false;
};

/**
 * Accept all consent types
 */
export const acceptAll = () => {
  setConsent({ analytics: true, functional: true });
};

/**
 * Reject all non-essential consent types
 */
export const rejectAll = () => {
  setConsent({ analytics: false, functional: true });
};
